import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthUser } from '@/api/types/auth.types';

/**
 * SECURITY NOTE:
 * - Tokens are stored in memory only (not persisted to localStorage/sessionStorage)
 * - This protects against XSS attacks that could steal tokens from storage
 * - Only basic user profile info is persisted (no sensitive data)
 * - For production, refresh tokens should be handled via httpOnly cookies set by the server
 * - The client should call a /refresh endpoint that reads the httpOnly cookie server-side
 */

interface AuthState {
  // State
  user: AuthUser | null;
  token: string | null;       // In-memory only, NOT persisted
  refreshToken: string | null; // In-memory only, NOT persisted (use httpOnly cookie in production)
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: AuthUser) => void;
  setTokens: (token: string, refreshToken: string) => void;
  login: (user: AuthUser, token: string, refreshToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  clearTokens: () => void;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      // Actions
      setUser: (user) => set({ user }),

      setTokens: (token, refreshToken) => set({ token, refreshToken }),

      login: (user, token, refreshToken) =>
        set({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () => {
        // Clear tokens from memory
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
        // Clear persisted storage
        sessionStorage.removeItem('tcy-auth-storage');
      },

      setLoading: (loading) => set({ isLoading: loading }),

      clearTokens: () => set({ token: null, refreshToken: null }),

      // Permission helpers
      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        return user.permissions?.includes(permission) || user.role === 'Admin';
      },

      hasRole: (role) => {
        const { user } = get();
        if (!user) return false;
        return user.role === role || user.role === 'Admin';
      },
    }),
    {
      name: 'tcy-auth-storage',
      // Use sessionStorage instead of localStorage (cleared when browser closes)
      storage: createJSONStorage(() => sessionStorage),
      // SECURITY: Only persist non-sensitive user profile data
      // Tokens are NOT persisted - they stay in memory only
      partialize: (state) => ({
        user: state.user ? {
          id: state.user.id,
          email: state.user.email,
          fullName: state.user.fullName,
          firstName: state.user.firstName,
          lastName: state.user.lastName,
          role: state.user.role,
          tenantId: state.user.tenantId,
          permissions: state.user.permissions,
          locale: state.user.locale,
          timezone: state.user.timezone,
        } : null,
        // Note: isAuthenticated will be false on page reload
        // User must re-authenticate or use silent refresh via httpOnly cookie
      }),
      // On rehydration, tokens will be null (not persisted)
      // The app should check with server via refresh token cookie
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isLoading = false;

          // Check for E2E test auth state in localStorage (Playwright stores state there)
          // This allows E2E tests to persist authentication across test files
          const e2eAuth = localStorage.getItem('tcy-auth-storage');
          if (e2eAuth) {
            try {
              const parsed = JSON.parse(e2eAuth);
              if (parsed.state?.token && parsed.state?.isAuthenticated) {
                state.token = parsed.state.token;
                state.refreshToken = parsed.state.refreshToken;
                state.isAuthenticated = true;
                state.user = parsed.state.user;
                return;
              }
            } catch {
              // Ignore parse errors
            }
          }

          // Normal flow: tokens are not persisted, authentication requires server check
          state.isAuthenticated = false;
          state.token = null;
          state.refreshToken = null;
        }
      },
    }
  )
);
